import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton, Chip
} from '@mui/material';
import { Home, Print, AccountBalance, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];

const Section = ({ title, rows, total, color }) => (
  <>
    <TableRow>
      <TableCell colSpan={2} sx={{ fontWeight: 800, color, pt: 2 }}>{title}</TableCell>
    </TableRow>
    {rows.length === 0 ? (
      <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>None</Typography></TableCell></TableRow>
    ) : rows.map((r, i) => (
      <TableRow key={(r.accountNo || 'x') + i} hover>
        <TableCell sx={{ pl: 3 }}>{r.accountNo ? `${r.accountNo} — ${r.accountName}` : r.accountName}</TableCell>
        <TableCell align="right">{fmt(r.amount)}</TableCell>
      </TableRow>
    ))}
    <TableRow>
      <TableCell sx={{ fontWeight: 700 }}>Total {title}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 700, borderTop: '1px solid #ccc' }}>{sym()} {fmt(total)}</TableCell>
    </TableRow>
  </>
);

const BalanceSheet = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [asOf, setAsOf]       = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/balance-sheet`, { headers: hdrs(), params: { asOf } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load balance sheet'); }
    finally { setLoading(false); }
  }, [asOf]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const acctLabel = (r) => (r.accountNo ? `${r.accountNo} — ${r.accountName}` : r.accountName);

  const exportExcel = () => {
    if (!data) return;

    const rows = [
      [company?.name || 'BALANCE SHEET'],
      ['BALANCE SHEET'],
      [`As of ${asOf}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Account', 'Amount'],
    ];

    const section = (title, list, total) => {
      rows.push([], [title.toUpperCase()]);
      if (!list.length) rows.push(['None', '']);
      else list.forEach(r => rows.push([acctLabel(r), r.amount]));
      rows.push([`Total ${title}`, total]);
    };

    section('Assets', data.assets, data.totals.assets);
    section('Liabilities', data.liabilities, data.totals.liabilities);
    section('Equity', data.equity, data.totals.equity);
    rows.push(
      [],
      ['TOTAL LIABILITIES + EQUITY', data.totals.liabilitiesAndEquity],
      ['Status', data.balanced ? 'Balanced' : 'Unbalanced'],
    );

    downloadCsv(rows, `balance_sheet_${asOf}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .bs-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="As of" value={asOf} size="small"
          onChange={e => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#1565c015' }}>
          {loading ? <CircularProgress size={20} /> : <AccountBalance />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="bs-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="BALANCE SHEET" subtitle={`As of ${asOf}`} />
          {data && (
            <>
              {!data.balanced && (
                <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
                  Assets do not equal Liabilities + Equity — check GL postings.
                </Alert>
              )}
              <Table size="small">
                <TableBody>
                  <Section title="Assets" rows={data.assets} total={data.totals.assets} color="#1565c0" />
                  <Section title="Liabilities" rows={data.liabilities} total={data.totals.liabilities} color="#c62828" />
                  <Section title="Equity" rows={data.equity} total={data.totals.equity} color="#6a1b9a" />
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 900 }}>
                      TOTAL LIABILITIES + EQUITY {data.balanced
                        ? <Chip label="Balanced" size="small" color="success" sx={{ ml: 1 }} />
                        : <Chip label="Unbalanced" size="small" color="error" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>{sym()} {fmt(data.totals.liabilitiesAndEquity)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BalanceSheet;
